import React, { useState, useEffect } from 'react';
import { useForm, Controller, SubmitHandler } from 'react-hook-form';
import Toggle from '~/components/Toggle';
import { RxCross2 } from 'react-icons/rx';
import { FaPlus } from 'react-icons/fa6';
import {
    fetchAssignees,
    fetchCampaigns,
    postMessage,
    createCampaignRequestNote,
} from '~/services/campaign';
import AsyncSelect from 'react-select/async';
import Select from 'react-select';
import { DropdownIndicatorProps, components } from 'react-select';
import { BsArrowUpRight } from 'react-icons/bs';
import { Link } from 'react-router-dom';
import TipTapEditor from '~/components/tiptap/TipTapEditor';
import toast from 'react-hot-toast';

enum ADD_MSG_TYPE {
    GENERAL = 'GENERAL',
    ONLY_TODO = 'ONLY_TODO',
    ONLY_NOTE = 'ONLY_NOTE',
}

interface Props {
    type: ADD_MSG_TYPE;
    campaign?: any;
    campaignRequest?: any;
    setNotes: any;
}

interface FormValues {
    todo_flag: boolean;
    assignTo: { label: string; value: string };
    campaign: string;
    note: string;
    tags: string;
}

const modules = {
    toolbar: [
        ['bold', 'italic', 'underline'],
        [{ align: 'right' }, { align: 'center' }],
        [{ list: 'ordered' }, { list: 'bullet' }],
        ['link', 'image'],
    ],
};

const formats = [
    'header',
    'bold',
    'italic',
    'underline',
    'strike',
    'blockquote',
    'list',
    'bullet',
    'indent',
    'link',
    'image',
];

const AddMessageModal: React.FC<Props> = ({
    type,
    campaign = null,
    campaignRequest = null,
    setNotes,
}) => {
    const [modalOpen, setModalOpen] = useState(false);
    const [isVisible, setIsVisible] = useState(false);
    const [campaignToOptions, setCampaignOptions] = useState([]);
    const [defaultCampaignId, setDefaultCampaignId] = useState('');
    const [assigneeOptions, setAssigneeOptions] = useState([]);
    const [saving, setSaving] = useState(false);

    const {
        control,
        handleSubmit,
        formState: { errors },
        watch,
        reset,
    } = useForm<FormValues>({
        defaultValues: { todo_flag: type === ADD_MSG_TYPE.ONLY_TODO },
        mode: 'onBlur',
    });
    const todo_flag = watch('todo_flag');

    const AddText = {
        [ADD_MSG_TYPE.GENERAL]: 'Message',
        [ADD_MSG_TYPE.ONLY_TODO]: 'To do',
        [ADD_MSG_TYPE.ONLY_NOTE]: 'Note',
    };

    const closeModal = () => setModalOpen(false);

    const onSubmit: SubmitHandler<FormValues> = async (data) => {
        if (!data?.note) return;

        try {
            setSaving(true);

            // If we have a campaign request (and no campaign), use the campaign request notes endpoint
            if (campaignRequest && !campaign?.campaign_id && !campaign?.id) {
                const notePayload = {
                    note: data.note,
                    assignment: data?.assignTo?.value
                        ? parseInt(data.assignTo.value)
                        : null,
                    todo_flag: data?.todo_flag || false,
                };

                const res = await createCampaignRequestNote(
                    campaignRequest.id,
                    notePayload,
                );

                // Keep the whole serialized note: campaign_request is what routes
                // later edits/pins/deletes to the CR note endpoints instead of
                // /messages (whose ids collide), and assignment fills the edit form.
                setNotes((prev: any) => [{ ...res.note, tags: '' }, ...prev]);
            } else {
                // Original message creation logic for campaigns
                const campaignId = data?.campaign?.value || defaultCampaignId;
                let campaignName = '';
                if (campaign) {
                    campaignName = campaign.campaign_name || campaign.name;
                } else {
                    const selectedCampaign = campaignToOptions.find(
                        (campaign) => {
                            return campaignId === campaign.value;
                        },
                    );
                    campaignName = selectedCampaign?.label;
                }

                const payload = {
                    campaign_name: campaignName || '',
                    note: data?.note,
                    todo_flag: data?.todo_flag,
                    pinned_flag: isVisible,
                    fc_acknowledged_flag: false,
                    created_date: new Date(),
                    campaign: campaignId || null,
                    assignee: data?.assignTo?.value
                        ? parseInt(data.assignTo.value)
                        : null,
                };

                const res = await postMessage(payload);
                setNotes((prev: any) => [res, ...prev]);
            }

            toast.success(
                data?.todo_flag
                    ? 'To Do created successfully'
                    : 'Note created successfully',
            );

            reset({ todo_flag: type === ADD_MSG_TYPE.ONLY_TODO });
        } catch (error) {
            console.log(error);
        } finally {
            setSaving(false);
        }

        closeModal();
    };

    useEffect(() => {
        const keyHandler = ({ keyCode }: KeyboardEvent) => {
            if (keyCode === 27) closeModal();
        };
        document.addEventListener('keydown', keyHandler);
        return () => document.removeEventListener('keydown', keyHandler);
    }, []);

    const fetchData = async () => {
        // If campaign is already provided, use it directly without fetching
        if (campaign?.campaign_id || campaign?.id) {
            const campaignId = campaign?.campaign_id || campaign?.id;
            const campaignName = campaign?.campaign_name || campaign?.name;

            setCampaignOptions([
                {
                    value: campaignId,
                    label: campaignName,
                },
            ]);
            setDefaultCampaignId(campaignId);
        } else if (campaign !== null) {
            // Only fetch campaigns if campaign is explicitly null (meaning we want to allow campaign selection)
            // If campaign is undefined, don't fetch anything
            const campaigns = await fetchCampaigns(
                null,
                { name: campaign?.campaign_name },
                null,
            );
            setCampaignOptions(
                campaigns?.results.map((campaign) => ({
                    value: campaign.campaign_id,
                    label: campaign.campaign_name,
                })),
            );
            setDefaultCampaignId(campaign?.campaign_id);
        }
        // If campaign is undefined, don't fetch or set any options
    };

    useEffect(() => {
        fetchData();
        loadAssigneeOptions();
    }, []);

    const loadCampaignOptions = async (inputValue: string) => {
        const campaigns = await fetchCampaigns(
            null,
            { name: inputValue },
            null,
        );
        return campaigns?.results.map((campaign) => ({
            value: campaign.campaign_id,
            label: campaign.campaign_name,
        }));
    };

    const loadAssigneeOptions = async () => {
        const assignees = await fetchAssignees();

        const mappedAssignees = assignees?.flatMap((group: any[]) => {
            const groupMembers = group[1];

            return groupMembers.map((member: any) => ({
                value: member[0],
                label: member[1],
            }));
        });

        setAssigneeOptions(mappedAssignees);
        return mappedAssignees;
    };

    const DropdownIndicator: React.FC<DropdownIndicatorProps> = (props) => {
        return (
            <components.DropdownIndicator {...props}>
                <svg
                    className='h-4 w-4 text-gray-500 dark:text-gray-400'
                    aria-hidden='true'
                    xmlns='http://www.w3.org/2000/svg'
                    fill='none'
                    viewBox='0 0 20 20'
                >
                    <path
                        stroke='currentColor'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth='2'
                        d='m19 19-4-4m0-7A7 7 0 1 1 1 8a7 7 0 0 1 14 0Z'
                    />
                </svg>
            </components.DropdownIndicator>
        );
    };

    const buttonClassName =
        type === ADD_MSG_TYPE.ONLY_TODO
            ? 'inline-flex shrink items-center justify-center gap-1 sm:gap-2 rounded-full border-[2px] border-[#FEBF3E] bg-[#FEBF3E] px-[12px] sm:px-[20px] py-[6px] sm:py-[7px] text-[11px]/[14px] sm:text-[13px]/[16px] font-bold uppercase text-black transition-colors duration-500 hover:border-[#E6A830] hover:bg-[#E6A830] whitespace-nowrap'
            : 'inline-flex shrink items-center justify-center gap-1 sm:gap-2 rounded-full border-[2px] border-black bg-white px-[12px] sm:px-[20px] py-[6px] sm:py-[7px] text-[11px]/[14px] sm:text-[13px]/[16px] font-bold uppercase text-black transition-colors duration-500 hover:bg-gray-100 whitespace-nowrap';

    return (
        <>
            <button
                onClick={() => setModalOpen(!modalOpen)}
                className={buttonClassName}
            >
                <FaPlus className='flex-shrink-0 text-[10px] sm:text-[12px]' />
                Add {AddText[type]}
            </button>

            {modalOpen && (
                <div
                    className='fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75'
                    onClick={closeModal}
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        className='relative w-full max-w-xl rounded-lg bg-white shadow-lg'
                    >
                        <button
                            className='absolute right-4 top-4 text-gray-600 hover:text-gray-900'
                            onClick={closeModal}
                        >
                            <RxCross2 />
                        </button>
                        <div className='p-8'>
                            <div className='mb-4'>
                                <h2 className='mb-[30px] text-[22px] font-bold'>
                                    Add {AddText[type]}
                                </h2>
                                <form onSubmit={handleSubmit(onSubmit)}>
                                    <div className='mb-4 flex items-start'>
                                        <Controller
                                            name='todo_flag'
                                            control={control}
                                            render={({ field }) => (
                                                <input
                                                    disabled={
                                                        type ===
                                                        ADD_MSG_TYPE.ONLY_TODO
                                                    }
                                                    checked={field.value}
                                                    onChange={(e) =>
                                                        field.onChange(
                                                            e.target.checked,
                                                        )
                                                    }
                                                    type='checkbox'
                                                    className='mr-2 mt-1.5 text-sm'
                                                    id='completed'
                                                />
                                            )}
                                        />
                                        <label
                                            className='text-xs'
                                            htmlFor='completed'
                                        >
                                            <span className='block text-base'>
                                                Mark as To Do
                                            </span>
                                            <span className='block'>
                                                If selected the message must be
                                                assigned to someone
                                            </span>
                                        </label>
                                    </div>
                                    {/* Determine if we're in Campaign Request mode (no campaign field needed) */}
                                    {campaignRequest &&
                                    !campaign?.campaign_id &&
                                    !campaign?.id ? (
                                        // Campaign Request mode - single column layout with only Assign To
                                        <div className='mb-4'>
                                            <label className='block text-xs font-semibold'>
                                                Assign To
                                                {todo_flag && (
                                                    <span className='text-red'>
                                                        {' '}
                                                        *
                                                    </span>
                                                )}
                                            </label>
                                            <div className='relative'>
                                                <div className='absolute inset-y-0 right-2.5 flex items-center'>
                                                    <svg
                                                        className='h-4 w-4 text-gray-500 dark:text-gray-400'
                                                        aria-hidden='true'
                                                        xmlns='http://www.w3.org/2000/svg'
                                                        fill='none'
                                                        viewBox='0 0 20 20'
                                                    >
                                                        <path
                                                            stroke='currentColor'
                                                            strokeLinecap='round'
                                                            strokeLinejoin='round'
                                                            strokeWidth='2'
                                                            d='m19 19-4-4m0-7A7 7 0 1 1 1 8a7 7 0 0 1 14 0Z'
                                                        />
                                                    </svg>
                                                </div>
                                                <Controller
                                                    name='assignTo'
                                                    control={control}
                                                    rules={{
                                                        validate: (value) => {
                                                            if (
                                                                watch(
                                                                    'todo_flag',
                                                                ) &&
                                                                !value
                                                            ) {
                                                                return 'Assignee is required when marked as To Do';
                                                            }
                                                            return true;
                                                        },
                                                    }}
                                                    render={({ field }) => (
                                                        <Select
                                                            menuPlacement='auto'
                                                            {...field}
                                                            placeholder='Select Assignee (Optional)'
                                                            isClearable
                                                            options={
                                                                assigneeOptions
                                                            }
                                                            styles={{
                                                                control: (
                                                                    baseStyles,
                                                                    state,
                                                                ) => ({
                                                                    ...baseStyles,
                                                                    borderColor:
                                                                        errors.assignTo
                                                                            ? 'red'
                                                                            : 'black',
                                                                    borderRadius:
                                                                        '0.375rem',
                                                                }),
                                                                menu: (
                                                                    baseStyles,
                                                                ) => ({
                                                                    ...baseStyles,
                                                                    zIndex: 9999,
                                                                }),
                                                            }}
                                                            components={{
                                                                IndicatorSeparator:
                                                                    null,
                                                                DropdownIndicator,
                                                            }}
                                                        />
                                                    )}
                                                />
                                            </div>
                                            {errors.assignTo && (
                                                <p className='mt-1 text-xs text-red'>
                                                    {errors.assignTo.message}
                                                </p>
                                            )}
                                        </div>
                                    ) : (
                                        // Campaign mode - two column layout with Assign To and Campaign
                                        <div className='mb-4 grid grid-cols-2 gap-4'>
                                            <div>
                                                <label className='block text-xs font-semibold'>
                                                    Assign To
                                                    {todo_flag && (
                                                        <span className='text-red'>
                                                            {' '}
                                                            *
                                                        </span>
                                                    )}
                                                </label>
                                                <div className='relative'>
                                                    <div className='absolute inset-y-0 right-2.5 flex items-center'>
                                                        <svg
                                                            className='h-4 w-4 text-gray-500 dark:text-gray-400'
                                                            aria-hidden='true'
                                                            xmlns='http://www.w3.org/2000/svg'
                                                            fill='none'
                                                            viewBox='0 0 20 20'
                                                        >
                                                            <path
                                                                stroke='currentColor'
                                                                strokeLinecap='round'
                                                                strokeLinejoin='round'
                                                                strokeWidth='2'
                                                                d='m19 19-4-4m0-7A7 7 0 1 1 1 8a7 7 0 0 1 14 0Z'
                                                            />
                                                        </svg>
                                                    </div>
                                                    <Controller
                                                        name='assignTo'
                                                        control={control}
                                                        rules={{
                                                            validate: (
                                                                value,
                                                            ) => {
                                                                if (
                                                                    watch(
                                                                        'todo_flag',
                                                                    ) &&
                                                                    !value
                                                                ) {
                                                                    return 'Assignee is required when marked as To Do';
                                                                }
                                                                return true;
                                                            },
                                                        }}
                                                        render={({ field }) => (
                                                            <Select
                                                                menuPlacement='auto'
                                                                {...field}
                                                                placeholder='Select Assignee (Optional)'
                                                                isClearable
                                                                options={
                                                                    assigneeOptions
                                                                }
                                                                styles={{
                                                                    control: (
                                                                        baseStyles,
                                                                        state,
                                                                    ) => ({
                                                                        ...baseStyles,
                                                                        borderColor:
                                                                            errors.assignTo
                                                                                ? 'red'
                                                                                : 'black',
                                                                        borderRadius:
                                                                            '0.375rem',
                                                                    }),
                                                                    menu: (
                                                                        baseStyles,
                                                                    ) => ({
                                                                        ...baseStyles,
                                                                        zIndex: 9999,
                                                                    }),
                                                                }}
                                                                components={{
                                                                    IndicatorSeparator:
                                                                        null,
                                                                    DropdownIndicator,
                                                                }}
                                                            />
                                                        )}
                                                    />
                                                </div>
                                                {errors.assignTo && (
                                                    <p className='mt-1 text-xs text-red'>
                                                        {
                                                            errors.assignTo
                                                                .message
                                                        }
                                                    </p>
                                                )}
                                            </div>
                                            {campaign &&
                                            (campaign.campaign_id ||
                                                campaign.id) ? (
                                                <div>
                                                    <label className='block text-xs font-semibold'>
                                                        Campaign
                                                    </label>
                                                    <Link
                                                        to={`/campaigns/${
                                                            campaign.campaign_id ||
                                                            campaign.id
                                                        }`}
                                                        target='_blank'
                                                        className='text-blue-500 mt-4 text-sm text-primary'
                                                    >
                                                        {campaign.campaign_name ||
                                                            campaign.name}{' '}
                                                        <BsArrowUpRight />
                                                    </Link>
                                                </div>
                                            ) : (
                                                <div>
                                                    <label className='block text-xs font-semibold'>
                                                        Campaign
                                                    </label>
                                                    <div className='relative'>
                                                        <Controller
                                                            name='campaign'
                                                            control={control}
                                                            render={({
                                                                field,
                                                            }) => (
                                                                <AsyncSelect
                                                                    {...field}
                                                                    cacheOptions
                                                                    placeholder='Select Campaign (Optional)'
                                                                    loadOptions={
                                                                        loadCampaignOptions
                                                                    }
                                                                    defaultOptions
                                                                    styles={{
                                                                        control:
                                                                            (
                                                                                baseStyles,
                                                                                state,
                                                                            ) => ({
                                                                                ...baseStyles,
                                                                                borderColor:
                                                                                    errors.campaign
                                                                                        ? 'red'
                                                                                        : 'black',
                                                                                borderRadius:
                                                                                    '0.375rem',
                                                                            }),
                                                                        menu: (
                                                                            baseStyles,
                                                                        ) => ({
                                                                            ...baseStyles,
                                                                            zIndex: 9999,
                                                                        }),
                                                                    }}
                                                                    components={{
                                                                        IndicatorSeparator:
                                                                            null,
                                                                        DropdownIndicator,
                                                                    }}
                                                                    {...field}
                                                                    isDisabled={
                                                                        campaign?.campaign_id
                                                                    }
                                                                    value={campaignToOptions.find(
                                                                        (
                                                                            option,
                                                                        ) =>
                                                                            option.value ===
                                                                            defaultCampaignId,
                                                                    )}
                                                                    onChange={(
                                                                        selectedOption,
                                                                    ) =>
                                                                        field.onChange(
                                                                            selectedOption,
                                                                        )
                                                                    }
                                                                />
                                                            )}
                                                        />
                                                    </div>
                                                    {errors.campaign && (
                                                        <p className='mt-1 text-xs text-red'>
                                                            {
                                                                errors.campaign
                                                                    .message
                                                            }
                                                        </p>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                    <div className='mb-4'>
                                        <label className='block text-xs font-semibold'>
                                            Message
                                        </label>
                                        <Controller
                                            control={control}
                                            name='note'
                                            rules={{
                                                required: 'Message is required',
                                            }}
                                            render={({
                                                field: {
                                                    onChange,
                                                    value,
                                                    onBlur,
                                                },
                                            }) => {
                                                return (
                                                    <TipTapEditor
                                                        initialValue={value}
                                                        onChange={onChange}
                                                    />
                                                );
                                            }}
                                        />
                                        {errors.note && (
                                            <p className='mt-1 text-xs text-red'>
                                                {errors.note.message}
                                            </p>
                                        )}
                                    </div>
                                    {/* Hide Pin toggle for campaign request notes */}
                                    {!(
                                        campaignRequest &&
                                        !campaign?.campaign_id &&
                                        !campaign?.id
                                    ) && (
                                        <div className='mb-4 flex items-center'>
                                            <Toggle
                                                isOn={isVisible}
                                                setIsOn={setIsVisible}
                                            />
                                            <span className='text-ms ml-2 font-normal'>
                                                Pin
                                            </span>
                                        </div>
                                    )}
                                    {/* <div className='mb-[30px]'>
                                        <label className='block text-xs font-semibold'>
                                            Tags
                                        </label>
                                        <Controller
                                            name='tags'
                                            control={control}
                                            defaultValue=''
                                            render={({ field }) => (
                                                <input
                                                    type='text'
                                                    className='border-gray-300 mt-1 w-full rounded-md border px-3 py-2 text-xs outline-none focus:border-primary'
                                                    placeholder='Add tag'
                                                    {...field}
                                                />
                                            )}
                                        />
                                        {errors.tags && (
                                            <p className='mt-1 text-xs text-red'>
                                                {errors.tags.message}
                                            </p>
                                        )}
                                    </div> */}
                                    <div className='flex justify-start space-x-4'>
                                        <button
                                            type='button'
                                            onClick={closeModal}
                                            className='rounded-full border-[2px] px-[24px] py-[7px] text-[14px]/[18px] font-semibold uppercase transition-colors duration-500 hover:border-black hover:bg-black hover:text-white'
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type='submit'
                                            disabled={saving}
                                            className='rounded-full bg-warning px-[24px] py-[7px] text-[14px]/[18px] font-semibold uppercase text-black transition-colors duration-500 hover:bg-yellow'
                                        >
                                            {saving ? 'Saving...' : 'Save'}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default AddMessageModal;
